import { DEFAULT_STORE_URL } from '@/lib/brand'

function trimTrailingSlash(url: string): string {
  return url.replace(/\/$/, '')
}

/** Primary store API origin. Empty string = same origin (/api via hosting or Vite proxy). */
export function getApiBase(): string {
  const configured = import.meta.env.VITE_API_URL?.trim()
  return configured ? trimTrailingSlash(configured) : ''
}

/** Deployed hosting URL — used as a dev fallback when the local API proxy is unavailable. */
export function getDevApiFallbackBase(): string {
  return trimTrailingSlash(DEFAULT_STORE_URL)
}

/**
 * Fetch a store API path, retrying against deployed hosting in dev when the local
 * proxy/API is down (common when running Vite without `npm run dev:api`).
 */
export async function fetchStoreApi(
  path: string,
  init?: RequestInit
): Promise<Response> {
  const bases = [getApiBase()]
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL?.trim()) {
    bases.push(getDevApiFallbackBase())
  }

  let lastResponse: Response | null = null

  for (let index = 0; index < bases.length; index += 1) {
    const base = bases[index]
    const url = `${base}${path}`

    try {
      const response = await fetch(url, init)
      lastResponse = response

      if (response.ok) {
        return response
      }

      const isLastBase = index === bases.length - 1
      // A 4xx response is a valid application/API response (for example,
      // Rehbar Post's 409 duplicate order error), not a reason to fall back
      // and replace the original error with a response from another server.
      if (isLastBase || (response.status >= 400 && response.status < 500)) {
        return response
      }
    } catch {
      const isLastBase = index === bases.length - 1
      if (isLastBase && lastResponse) {
        return lastResponse
      }
      if (isLastBase) {
        throw new Error('Network error')
      }
    }
  }

  throw new Error('Network error')
}
